/**
 * REST client for the directory-integration / identity surface
 * (`/v1/directory-integrations…`, `/v1/directory/*`). Mirrors the
 * legacy dashboard's `api/identity.ts` but routes through origin's
 * `authedFetch` so bearer + 401-refresh behavior matches the rest
 * of the gateway calls.
 *
 * Field names map proto camelCase ↔ snake_case at the boundary.
 * The wire format wraps `config` under a oneof variant keyed by
 * provider (entra_id / okta / google_workspace) — callers pass a
 * flat config object and `createDirectoryIntegration` shapes it.
 */
import { restGet, restPost, restPatch, restDelete, idForPath } from "./restClient.js";

// Frontend provider keys ↔ proto enum values.
export const PROVIDER_TO_BACKEND = {
    ENTRA_ID: "DIRECTORY_PROVIDER_TYPE_ENTRA_ID",
    OKTA:     "DIRECTORY_PROVIDER_TYPE_OKTA",
    GOOGLE:   "DIRECTORY_PROVIDER_TYPE_GOOGLE_WORKSPACE",
};

// Accept both the enum form and the legacy snake_case form returned
// by older list responses so the UI tolerates a backend mid-rollout.
const BACKEND_TO_PROVIDER = {
    DIRECTORY_PROVIDER_TYPE_ENTRA_ID: "ENTRA_ID",
    DIRECTORY_PROVIDER_TYPE_OKTA: "OKTA",
    DIRECTORY_PROVIDER_TYPE_GOOGLE_WORKSPACE: "GOOGLE",
    entra_id: "ENTRA_ID",
    okta: "OKTA",
    google_workspace: "GOOGLE",
};

// Strip the proto enum prefix and lowercase what remains, so
// `DIRECTORY_INTEGRATION_STATUS_AT_RISK` → `at_risk` and the UI can
// render a short, kebab/snake-case label. Falls through for any
// value that doesn't carry the prefix (legacy short-form responses,
// future schema additions we haven't classified yet).
function normalizeStatus(raw) {
    if (!raw || typeof raw !== "string") return raw;
    const m = raw.match(/^DIRECTORY_INTEGRATION_STATUS_(.+)$/);
    if (m) return m[1].toLowerCase();
    return raw;
}

function normalizeProvider(raw) {
    if (!raw || typeof raw !== "string") return raw;
    return BACKEND_TO_PROVIDER[raw] ?? raw;
}

const PROVIDER_DISPLAY = {
    ENTRA_ID: "Entra ID",
    OKTA: "Okta",
    GOOGLE: "Google",
};

export function providerLabel(raw) {
    const normalized = normalizeProvider(raw);
    return PROVIDER_DISPLAY[normalized] ?? normalized ?? "";
}

// The same provider has three spellings and the API picks one per field: a
// `DirectoryProviderType` enum field takes the full value name, a plain-string
// field is compared byte-exactly against the stored snake_case, and
// `normalizeProvider` produces neither. Sending the wrong one does not fail
// type-checking anywhere, so route every write through these.
const PROVIDER_DB_STRING = {
    ENTRA_ID: "entra_id",
    OKTA: "okta",
    GOOGLE: "google_workspace",
};

const PROVIDER_WIRE_ENUM = {
    ENTRA_ID: "DIRECTORY_PROVIDER_TYPE_ENTRA_ID",
    OKTA: "DIRECTORY_PROVIDER_TYPE_OKTA",
    GOOGLE: "DIRECTORY_PROVIDER_TYPE_GOOGLE_WORKSPACE",
};

export function providerToDbString(raw) {
    return PROVIDER_DB_STRING[normalizeProvider(raw)] ?? "";
}

export function providerToWireEnum(raw) {
    return PROVIDER_WIRE_ENUM[normalizeProvider(raw)] ?? "DIRECTORY_PROVIDER_TYPE_UNSPECIFIED";
}

// `DIRECTORY_SYNC_SCOPE_NOT_CONFIGURED` → `not_configured`, etc. Absent on a
// pre-field (deploy-skew) backend, in which case the row shows no scope badge.
function normalizeSyncScope(raw) {
    if (!raw || typeof raw !== "string") return raw;
    const m = raw.match(/^DIRECTORY_SYNC_SCOPE_(.+)$/);
    if (m) return m[1].toLowerCase();
    return raw;
}

// `DIRECTORY_SYNC_STATUS_COMPLETED` → `completed`,
// `DIRECTORY_SYNC_TYPE_INCREMENTAL` → `incremental`. The history view keys its
// status palette off the short form, so without these every run renders in the
// neutral palette with a raw proto constant as its label.
function normalizeSyncStatus(raw) {
    if (!raw || typeof raw !== "string") return raw;
    const m = raw.match(/^DIRECTORY_SYNC_STATUS_(.+)$/);
    if (m) return m[1].toLowerCase();
    return raw;
}

function normalizeSyncType(raw) {
    if (!raw || typeof raw !== "string") return raw;
    const m = raw.match(/^DIRECTORY_SYNC_TYPE_(.+)$/);
    if (m) return m[1].toLowerCase();
    return raw;
}

// Sync-scope picker surfaces both: `scoped` (an Entra group selection) and
// `full` (the `sync_full_tenant` config flag). `incremental` stays backend-only.
const SYNC_TYPE_TO_BACKEND = {
    scoped: "DIRECTORY_SYNC_TYPE_SCOPED",
    full: "DIRECTORY_SYNC_TYPE_FULL",
    incremental: "DIRECTORY_SYNC_TYPE_INCREMENTAL",
};

// `sync_full_tenant` and `is_primary` are siblings of the `config` oneof on
// `DirectoryProviderConfig`, not fields inside the provider variant — so they
// ride alongside `entra_id` / `okta` / `google_workspace`, never inside them.
// Omitting `is_primary` leaves the stored value alone; `false` unmarks.
function providerConfigPayload(provider, config, syncFullTenant, isPrimary) {
    const payload = (() => {
        switch (provider) {
            case "ENTRA_ID": return { entra_id: config };
            case "OKTA":     return { okta: config };
            case "GOOGLE":   return { google_workspace: config };
            default:         return {};
        }
    })();
    if (typeof syncFullTenant === "boolean") payload.sync_full_tenant = syncFullTenant;
    if (typeof isPrimary === "boolean") payload.is_primary = isPrimary;
    return payload;
}

export async function listDirectoryIntegrations() {
    const result = await restGet("/v1/directory-integrations");
    if (result?.integrations) {
        for (const i of result.integrations) {
            i.provider  = normalizeProvider(i.provider);
            i.status    = normalizeStatus(i.status);
            i.syncScope = normalizeSyncScope(i.syncScope);
        }
    }
    return result ?? { integrations: [] };
}

export function createDirectoryIntegration({ displayName, provider, config, credentials, copyFromCopilotIntegrationId }) {
    const body = {
        display_name: displayName,
        provider: PROVIDER_TO_BACKEND[provider] ?? provider,
        config: providerConfigPayload(provider, config),
        credentials: credentials ?? {},
    };
    if (copyFromCopilotIntegrationId) body.copy_from_copilot_integration_id = copyFromCopilotIntegrationId;
    return restPost("/v1/directory-integrations", body);
}

// Status is deliberately not settable here — `setDirectoryIntegrationEnabled` owns
// the only two status writes a client may make.
export function updateDirectoryIntegration(id, { displayName, provider, config, credentials, syncFullTenant, isPrimary }) {
    const body = {
        integration_id: id,
        display_name: displayName,
        credentials: credentials ?? {},
    };
    // Only send `config` when a full provider config (or the whole-tenant flag)
    // is supplied. The backend replaces the non-secret config in full, so a
    // partial would wipe stored fields — callers that only touch the display name
    // must omit it, and callers that set `syncFullTenant` must pass the COMPLETE
    // provider config so the flag doesn't clear the rest.
    if (provider && ((config && Object.keys(config).length > 0)
        || typeof syncFullTenant === "boolean" || typeof isPrimary === "boolean")) {
        body.config = providerConfigPayload(provider, config ?? {}, syncFullTenant, isPrimary);
    }
    return restPatch(`/v1/directory-integrations/${idForPath(id)}`, body);
}

// Stop / resume syncing. These are the only two status writes the backend accepts,
// and it validates them as transitions — see `resolve_status_transition`.
//
// Sends the full proto enum names, not the short forms `normalizeStatus` produces
// on the way in: the gateway parses the wire enum, and the short form is a
// rendering convenience that exists in one direction only.
export function setDirectoryIntegrationEnabled(id, enabled) {
    return restPatch(`/v1/directory-integrations/${idForPath(id)}`, {
        integration_id: id,
        status: enabled
            ? "DIRECTORY_INTEGRATION_STATUS_CONNECTED"
            : "DIRECTORY_INTEGRATION_STATUS_DISABLED",
    });
}

// Fetch a single integration including its non-secret provider config (the
// `config` oneof, e.g. `{ entra_id: { tenant_id, ..., synced_group_ids } }`).
// Used by the group-scope picker to load the current config before merging in
// the new selection (the update replaces config in full).
// Status and provider are normalized to the shapes the list returns, so a caller
// need not know which call produced them: the raw enums reached callers here alone,
// which made `status === "pending"` silently never match.
export async function getDirectoryIntegration(id) {
    const detail = await restGet(`/v1/directory-integrations/${idForPath(id)}`);
    if (detail?.integration) {
        detail.integration.status = normalizeStatus(detail.integration.status);
        detail.integration.provider = normalizeProvider(detail.integration.provider);
    }
    return detail;
}

export function deleteDirectoryIntegration(id) {
    return restDelete(`/v1/directory-integrations/${idForPath(id)}`);
}

export function testDirectoryIntegration(id) {
    return restPost(`/v1/directory-integrations:test`, { integration_id: id });
}

// List an integration's groups live from the IdP (no prior sync needed), for
// the group-scope picker — server-side `search` + cursor pagination, mirroring
// `listDirectoryGroups`. Returns `{ groups: [{ id, displayName }], nextPageToken,
// truncated }`; pass the chosen ids straight back into the Entra config's
// `syncedGroupIds` (opaque string, no conversion). `JSON.stringify` drops the
// `undefined` keys, so empty options aren't sent.
export async function previewDirectoryGroups(id, { search, pageSize, pageToken } = {}) {
    const r = await restPost(`/v1/directory-integrations:preview-groups`, {
        integration_id: id,
        search: search || undefined,
        page_size: pageSize || undefined,
        page_token: pageToken || undefined,
    });
    return r ?? { groups: [], nextPageToken: "", truncated: false };
}

export function triggerDirectorySync(id, syncType = "scoped") {
    return restPost(`/v1/directory-integrations:sync`, {
        integration_id: id,
        sync_type: SYNC_TYPE_TO_BACKEND[syncType] ?? syncType,
    });
}

export function getDirectoryAuthUrl({ integrationId, redirectUri, code = "", state = "" }) {
    return restPost(`/v1/directory-integrations:auth-url`, {
        integration_id: integrationId,
        redirect_uri: redirectUri,
        code,
        state,
    });
}

export function completeEntraIdminConsent({ integrationId, state, adminConsent, tenant }) {
    return restPost(`/v1/directory-integrations:complete-consent`, {
        integration_id: integrationId,
        admin_consent: adminConsent,
        tenant_id: tenant,
        state,
    });
}

// ── Directory browser endpoints ───────────────────────────────────

// Strip undefined/empty values from a params object before passing
// to restGet — the legacy `provider: '' `style filter all-off shouldn't
// pollute the URL.
function cleanParams(params) {
    if (!params) return null;
    const out = {};
    for (const [k, v] of Object.entries(params)) {
        if (v == null || v === "") continue;
        if (k === "provider" && PROVIDER_TO_BACKEND[v]) out[k] = PROVIDER_TO_BACKEND[v];
        else out[k] = v;
    }
    return out;
}

// Normalize the proto enum `sourceProvider` so the cell renderers can
// look it up via PROVIDER_LABEL without each cell having to know the
// full enum form.
function normalizeBrowserList(list, key) {
    if (!Array.isArray(list)) return list;
    for (const item of list) {
        item.sourceProvider = normalizeProvider(item.sourceProvider);
    }
    return list;
}

export async function listDirectoryUsers(params) {
    const r = await restGet("/v1/directory/users", cleanParams(params));
    normalizeBrowserList(r?.users);
    return r ?? { users: [], nextPageToken: "", totalCount: 0 };
}

export async function listDirectoryGroups(params) {
    const r = await restGet("/v1/directory/groups", cleanParams(params));
    normalizeBrowserList(r?.groups);
    return r ?? { groups: [], nextPageToken: "", totalCount: 0 };
}

export async function listDirectoryDevices(params) {
    const r = await restGet("/v1/directory/devices", cleanParams(params));
    normalizeBrowserList(r?.devices);
    return r ?? { devices: [], nextPageToken: "", totalCount: 0 };
}

// Inbound responses normalize provider to the short form ("ENTRA_ID"
// etc.) so the UI can look up labels off a single key. Outbound
// requests need the proto enum form ("DIRECTORY_PROVIDER_TYPE_…"); if
// the caller already passed the proto form it's preserved verbatim.
function providerToBackend(p) {
    if (!p) return p;
    return PROVIDER_TO_BACKEND[p] ?? p;
}

// Paging is trailing and optional: the Devices table's owner lookup wants none.
export async function getDirectoryUser(
    sourceProvider,
    sourceId,
    { groupPageSize, groupPageToken, devicePageSize, devicePageToken } = {},
) {
    const r = await restPost("/v1/directory/users:get", {
        source_provider: providerToBackend(sourceProvider),
        source_id: sourceId,
        group_page_size: groupPageSize,
        group_page_token: groupPageToken,
        device_page_size: devicePageSize,
        device_page_token: devicePageToken,
    });
    if (r) {
        r.sourceProvider = normalizeProvider(r.sourceProvider);
        if (Array.isArray(r.groups))  normalizeBrowserList(r.groups);
        if (Array.isArray(r.devices)) normalizeBrowserList(r.devices);
    }
    return r;
}

/**
 * Owners are not paginated — the set is small — so only `groups` carries a cursor.
 * `owners` is the subset of `ownerRef` that resolved to a synced user, so a scoped
 * sync makes it shorter.
 */
export async function getDirectoryDevice({ sourceProvider, sourceId, groupPageSize, groupPageToken }) {
    const r = await restPost("/v1/directory/devices:get", {
        source_provider: providerToBackend(sourceProvider),
        source_id: sourceId,
        group_page_size: groupPageSize,
        group_page_token: groupPageToken,
    });
    if (r) {
        r.sourceProvider = normalizeProvider(r.sourceProvider);
        if (Array.isArray(r.owners)) normalizeBrowserList(r.owners);
        if (Array.isArray(r.groups)) normalizeBrowserList(r.groups);
    }
    return r;
}

export async function getDirectoryGroup({
    sourceProvider,
    sourceId,
    userPageSize,
    userPageToken,
    devicePageSize,
    devicePageToken,
    subGroupPageSize,
    subGroupPageToken,
}) {
    const r = await restPost("/v1/directory/groups:get", {
        source_provider: providerToBackend(sourceProvider),
        source_id: sourceId,
        user_page_size: userPageSize,
        user_page_token: userPageToken,
        device_page_size: devicePageSize,
        device_page_token: devicePageToken,
        sub_group_page_size: subGroupPageSize,
        sub_group_page_token: subGroupPageToken,
    });
    if (r) {
        r.sourceProvider = normalizeProvider(r.sourceProvider);
        if (Array.isArray(r.users))     normalizeBrowserList(r.users);
        if (Array.isArray(r.devices))   normalizeBrowserList(r.devices);
        if (Array.isArray(r.subGroups)) normalizeBrowserList(r.subGroups);
    }
    return r;
}

// ── Sync history + endpoint→user assignments (O-1162 parity) ─────
//
// Two read-only endpoints the legacy dashboard surfaced that Origin
// hadn't yet wired:
//   - listDirectorySyncHistory: per-integration sync run history
//     (status, durations, per-resource counts, errors).
//   - getEndpointUserAssignments: which directory users are mapped to
//     a given endpoint and which signal (serial/hostname/SID) matched.

export async function listDirectorySyncHistory(integrationId, { pageSize, pageToken } = {}) {
    const r = await restGet(
        `/v1/directory-integrations/${idForPath(integrationId)}/sync-history`,
        { page_size: pageSize, page_token: pageToken },
    );
    for (const e of r?.entries ?? []) {
        e.status = normalizeSyncStatus(e.status);
        e.syncType = normalizeSyncType(e.syncType);
    }
    return r ?? { entries: [], nextPageToken: "" };
}

export async function getEndpointUserAssignments(endpointId) {
    return restPost("/v1/directory/endpoint-users:get", { endpoint_id: endpointId });
}

function manualAssignmentResponse(raw) {
    if (!raw) throw new Error("The assignment response is unavailable. Refresh to try again.");
    return {
        ...raw,
        revision: String(raw.revision ?? "0"),
        status: String(raw.status ?? "UNAVAILABLE")
            .replace(/^ENDPOINT_MANUAL_ASSIGNMENT_STATUS_/, "").toLowerCase(),
        canAssign: raw.canAssign === true,
        assignment: raw.assignment ? {
            ...raw.assignment,
            sourceProvider: normalizeProvider(raw.assignment.sourceProvider),
        } : null,
    };
}

export async function getEndpointManualAssignment(endpointId) {
    return manualAssignmentResponse(await restPost("/v1/directory/endpoint-users:manual-get", {
        endpoint_id: endpointId,
    }));
}

export async function setEndpointManualAssignment(endpointId, { user, expectedRevision, requestId }) {
    return manualAssignmentResponse(await restPost("/v1/directory/endpoint-users:manual-set", {
        endpoint_id: endpointId,
        integration_id: user.integrationId,
        source_provider: providerToWireEnum(user.sourceProvider),
        source_id: user.sourceId,
        expected_revision: expectedRevision,
        request_id: requestId,
    }));
}

export async function removeEndpointManualAssignment(endpointId, { expectedRevision, requestId }) {
    return manualAssignmentResponse(await restPost("/v1/directory/endpoint-users:manual-remove", {
        endpoint_id: endpointId,
        expected_revision: expectedRevision,
        request_id: requestId,
    }));
}
